import React, { ReactNode, useEffect, useState } from "react";
import { AppServices } from "../../../AppServices";
import { SurveyData, SurveyRequestResponseHistoryEntry, SurveyRequestResponseHistoryInventoryData } from "../../../store/Models/Surveys/SurveyDataModel";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";

interface SurveyMaterialsSectionProps {
    availableMaterials: SurveyRequestResponseHistoryInventoryData[];
    unavailableMaterials: SurveyRequestResponseHistoryInventoryData[];
    appServices: AppServices;
    surveyMetadata: SurveyData;
    renderAvailblematerials: () => ReactNode;
    historyEntry: SurveyRequestResponseHistoryEntry;
    selectAllMaterials: (historyEntry: SurveyRequestResponseHistoryEntry, isChecked: boolean) => void;
    acceptSelectedMaterials: (historyEntry: SurveyRequestResponseHistoryEntry, markAsApproved: boolean) => void;
}

const SurveyMaterialsSection: React.FC<SurveyMaterialsSectionProps> = ({
    availableMaterials,
    unavailableMaterials,
    appServices,
    surveyMetadata,
    renderAvailblematerials,
    historyEntry,
    selectAllMaterials,
    acceptSelectedMaterials,
}) => {
    const [isAvailableSectionOpen, setIsAvailableSectionOpen] = useState(true);
    const [isUnavailableSectionOpen, setIsUnavailableSectionOpen] = useState(false);
    const toxnotServiceClient = new ToxnotServiceClient();

    useEffect(() => {
        const updateSelectAllCheckbox = (checkboxSelector: string, selectAllSelector: string) => {
            const $checkboxes = $(checkboxSelector);
            const $selectAllCheckbox = $(selectAllSelector);
            
            if ($checkboxes.length === 0) return;
            
            let allChecked = true;
            let anyChecked = false;
            
            $checkboxes.each(function() {
                const $checkbox = $(this);
                if (!$checkbox.prop('disabled') && !$checkbox.prop('checked')) {
                    allChecked = false;
                }
                if ($checkbox.prop('checked')) {
                    anyChecked = true;
                }
            });
            
            $selectAllCheckbox.prop('checked', allChecked);
            $selectAllCheckbox.prop('indeterminate', anyChecked && !allChecked);
        };

        // For unavailable materials
        const updateUnavailableSelectAllCheckbox = () => {
            updateSelectAllCheckbox(
                `.unavailable-material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`,
                '.unavailable-material-select-all-checkbox'
            );
        };

        // For available materials
        const updateAvailableSelectAllCheckbox = () => {
            updateSelectAllCheckbox(
                `.material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`,
                '.material-select-all-checkbox'
            );
        };

        $(document).on('change', `.unavailable-material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`, updateUnavailableSelectAllCheckbox);
        $(document).on('change', `.material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`, updateAvailableSelectAllCheckbox);

        // Initial check
        updateUnavailableSelectAllCheckbox();
        updateAvailableSelectAllCheckbox();

        // Clean up event listeners when component unmounts
        return () => {
            $(document).off('change', `.unavailable-material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`, updateUnavailableSelectAllCheckbox);
            $(document).off('change', `.material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`, updateAvailableSelectAllCheckbox);
        };
    }, [historyEntry.historyId]);

    const selectAllUnavailableMaterialsCheckbox_change = (historyEntry: SurveyRequestResponseHistoryEntry, checked: boolean) => {
        const $checkboxes = $(`.unavailable-material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]`);

        for (var i = 0; i < $checkboxes.length; i++) {
            let invId = $checkboxes.eq(i).data("response-inventory-id");

            if (surveyMetadata.offlineProcessQueuedMaterials.indexOf(invId) === -1) {
                $checkboxes.eq(i).prop("checked", checked);
            }
        }
    }

    const openModelToAssignStatusToUnavailableMaterials = async () => {
        const $selectedCheckboxes = $(`.unavailable-material-name-checkbox[data-history-entry-id="${historyEntry.historyId}"]:checked`);

        if ($selectedCheckboxes.length === 0) {
            alert(appServices.localizeString("Please select at least one material in the response."));
            return;
        }
        const response = await toxnotServiceClient.getInventoryStatusDefinations(appServices.userContext?.teamOwnerId!);

        if (!response.success || response.data === null) {
            alert(appServices.localizeString("Sorry, unable to fetch data from service."));
            return;
        }

        const data = response.data as string[];
        const $modal = $("#assign-status-modal");
        
        const container = $("#assign-status-modal-status-options");
        container.empty();

        data.forEach((key) => {
            const checkboxHtml = `
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="productStatusCheckbox" value="${key}" class="status-checkbox">
                        <span class="status-label">${key}</span>
                    </label>
                </div>
            `;
            container.append(checkboxHtml);
        });
    
        container.find('.status-checkbox').on('change', function() {
            if ($(this).is(':checked')) {
                container.find('.status-checkbox').not(this).prop('checked', false);
            }
        });
    
        $modal.modal("show");
        $(".success-alert, .error-alert").hide();
    }

    return (
        <>
            <div className="all-materials-container">
                {unavailableMaterials.length > 0 && (
                    <div className="unavailable-materials-section">
                        <div className="section-header">
                            <div className="title">
                                <div className="header-content">
                                    <span 
                                        className={`toggle-icon ${isUnavailableSectionOpen ? 'open' : 'closed'}`}
                                        onClick={() => setIsUnavailableSectionOpen(!isUnavailableSectionOpen)}
                                    >
                                        {isUnavailableSectionOpen ? '▼' : '▶'}
                                    </span>
                                    <div className="checkbox">
                                        <label>
                                            <input 
                                                className="unavailable-material-select-all-checkbox" 
                                                type="checkbox" 
                                                onChange={e => {
                                                    e.stopPropagation();
                                                    selectAllUnavailableMaterialsCheckbox_change(historyEntry, e.currentTarget.checked);
                                                }} 
                                                disabled={!appServices.userContext?.isEditor} 
                                            /> 
                                            <b>{appServices.localizeString("Unavailable Materials")} ({unavailableMaterials.length})</b>
                                        </label>
                                    </div>
                                    <span className="info-icon">
                                        <i 
                                            className="glyphicon glyphicon-info-sign" 
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            title={appServices.localizeString("The supplier indicated that one or more products in this survey are no longer available or were never sold. You can review and assign a status to these materials below.")}
                                        ></i>
                                    </span>
                                </div>
                                <button 
                                    className="btn btn-sm btn-default assign-status-btn"
                                    onClick={openModelToAssignStatusToUnavailableMaterials}
                                >
                                    {appServices.localizeString("Assign Status to Selected")}
                                </button>
                            </div>
                        </div>
                        
                        <div 
                            className={`unavailable-materials-info ${isUnavailableSectionOpen ? 'visible' : 'hidden'}`}
                        >
                            {unavailableMaterials.map((material, index) => (
                                <div className="top align-center" key={index}>
                                    <div className="material-name">
                                        <div className="checkbox">
                                            <label>
                                                <input 
                                                    type="checkbox" 
                                                    className="unavailable-material-name-checkbox" 
                                                    data-history-entry-id={historyEntry.historyId} 
                                                    data-response-inventory-id={material.responseInventoryId} 
                                                    data-target-inventory-id={material.targetInventoryId}
                                                    disabled={!appServices.userContext?.isEditor || (surveyMetadata.offlineProcessQueuedMaterials.includes(material.responseInventoryId) ? true: false)} 
                                                /> 
                                                <div className="unavailable-material-item">
                                                    <a 
                                                        href={`Data/Inventory?inventoryId=${material.targetInventoryId}`}
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                    >
                                                        {/* splitting inventoryName as it's sending inventory name + description */}
                                                        {surveyMetadata?.responseInventoryMetadataTable[material.responseInventoryId]?.inventoryName.split(" - ")[0]} 
                                                    </a>
                                                    {" - "}
                                                    {surveyMetadata?.responseInventoryMetadataTable[material.responseInventoryId]?.description}
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {availableMaterials.length > 0 && (
                    <div className="available-materials-section">
                        <div className="section-header">
                            <div className="title">
                                <div className="header-content">
                                    <span 
                                        className="toggle-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsAvailableSectionOpen(!isAvailableSectionOpen);
                                        }}
                                    >
                                        {isAvailableSectionOpen ? '▼' : '▶'}
                                    </span>
                                    <div className="checkbox">
                                        <label>
                                            <input 
                                                className="material-select-all-checkbox" 
                                                type="checkbox" 
                                                onChange={e => {
                                                    e.stopPropagation();
                                                    selectAllMaterials(historyEntry, e.currentTarget.checked);
                                                }} 
                                                disabled={!appServices.userContext?.isEditor} 
                                            /> 
                                            <b>{appServices.localizeString("Available Materials")}</b>
                                        </label>
                                    </div>
                                </div>
                                {historyEntry && historyEntry.inventoryDataList && historyEntry.inventoryDataList.length > 1 && appServices.userContext?.isEditor && (
                                    appServices.userContext?.userCapabilities.lifecycleStatusEnabled
                                        ? (
                                            <div className="btn-group">
                                                <button type="button" className="btn btn-sm btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                                    {appServices.localizeString("Accept Selected Materials")}
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-right">
                                                    <li><a role="button" onClick={e => acceptSelectedMaterials(historyEntry, false)}>{appServices.localizeString("Accept Selected Materials")}</a></li>
                                                    <li><a role="button" onClick={e => acceptSelectedMaterials(historyEntry, true)}>{appServices.localizeString("Accept Selected Materials and Mark as Approved")}</a></li>
                                                </ul>
                                            </div>
                                        )
                                        : (
                                            <button 
                                                type="button" 
                                                id="accept-selected-materials-button" 
                                                className="btn btn-sm btn-default" 
                                                onClick={e => acceptSelectedMaterials(historyEntry, false)}
                                            >
                                                {appServices.localizeString("Accept Selected Materials")}
                                            </button>
                                        )
                                )}
                            </div>
                        </div>
                        
                        <div 
                            className={`unavailable-materials-info ${isAvailableSectionOpen ? 'visible' : 'hidden'}`}
                        >
                            {renderAvailblematerials()}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SurveyMaterialsSection;
